import { useState } from "react";
import { UserXIcon } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, EmptyState, ErrorText } from "@/components";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { IntakeGuidance } from "@/components/companies/shared/IntakeGuidance";
import { IntakeTab } from "@/components/companies/shared/IntakeTab";
import { IntakeTabBar } from "@/components/companies/shared/IntakeTabBar";
import { ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { EMPTY_PROGRESS } from "@/lib/constants";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { AntiIdealCandidateProfile, JobIntake, Progress } from "@/types";

const profileSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  commonFailurePatterns: z.string().nullable(),
  backgroundAndExperienceRedFlags: z.string().nullable(),
  personalityRedFlags: z.string().nullable(),
  extraDetails: z.string().nullable(),
});

const emptyProfile = (): AntiIdealCandidateProfile => ({
  id: crypto.randomUUID(),
  title: null,
  commonFailurePatterns: null,
  backgroundAndExperienceRedFlags: null,
  personalityRedFlags: null,
  extraDetails: null,
});

export const AntiIdealCandidateProfiles = ({
  isEditing,
  jobIntake,
  setIsEditing,
  itemsProgress,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
  setIsEditing: (isEditing: boolean) => void;
  itemsProgress: Record<string, Progress>;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  /* React Hooks */
  // useState
  const [profiles, setProfiles] = useState<AntiIdealCandidateProfile[]>(
    jobIntake.antiIdealCandidateProfiles,
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  /* Handlers */
  const saveAll = (updated: AntiIdealCandidateProfile[]) => {
    updateJobIntake({
      updateJobIntakeRequest: { antiIdealCandidateProfiles: updated },
    });
  };

  const handleAdd = () => {
    const updated = [...profiles, emptyProfile()];
    setProfiles(updated);
    setSelectedIndex(updated.length - 1);
    setIsEditing(true);
    saveAll(updated);
  };

  const handleDelete = (id: string) => {
    const deletedIndex = profiles.findIndex((p) => p.id === id);
    const updated = profiles.filter((p) => p.id !== id);
    setProfiles(updated);
    const newIndex =
      deletedIndex < selectedIndex ? selectedIndex - 1 : selectedIndex;
    setSelectedIndex(Math.min(Math.max(0, newIndex), updated.length - 1));
    saveAll(updated);
  };

  const handleUpdate = (data: AntiIdealCandidateProfile) => {
    const updated = profiles.map((p) => (p.id === data.id ? data : p));
    setProfiles(updated);
    saveAll(updated);
  };

  if (!profiles.length) {
    return (
      <Column className="gap-6">
        <EmptyState
          title="No anti-ideal candidate profiles yet"
          description="Add a profile to define what to avoid in candidates."
          Icon={UserXIcon}
          primaryButtonName="Add profile"
          handlePrimaryClick={() => {
            setIsEditing(true);
            handleAdd();
          }}
        />
      </Column>
    );
  }

  return (
    <Column className="gap-4">
      {/* top offset = combined height of IntakeHeader */}
      <Column className="sticky top-[8.78rem] z-10 gap-4 bg-white pb-2">
        <IntakeGuidance message="Reflect on past hires that didn't work out. What about their background, experience, or personality wasn't the right fit?" />
        <IntakeTabBar
          addLabel="Add Profile"
          deleteDescription="This will permanently delete this anti-ideal candidate profile."
          onAdd={handleAdd}
          onDelete={() => {
            const selected = profiles[selectedIndex];
            handleDelete(selected.id);
          }}
          screenName={MIXPANEL_SCREENS.JOB}
          trackingName="Anti-Ideal Candidate Profile"
        >
          {profiles.map((profile, index) => (
            <IntakeTab
              key={profile.id}
              progress={itemsProgress[profile.id] ?? EMPTY_PROGRESS}
              isSelected={selectedIndex === index}
              label={profile.title ?? `Anti ICP ${String(index + 1)}`}
              onSelect={() => {
                setSelectedIndex(index);
              }}
            />
          ))}
        </IntakeTabBar>
      </Column>
      {isEditing ? (
        <ProfileForm
          key={profiles[selectedIndex]?.id ?? "empty"}
          onSave={handleUpdate}
          profile={profiles[selectedIndex] ?? emptyProfile()}
        />
      ) : (
        <ProfileReadOnly profile={profiles[selectedIndex] ?? emptyProfile()} />
      )}
      {isError && (
        <ErrorText errorMessage="An error occurred while saving. Please try again." />
      )}
    </Column>
  );
};

const ProfileForm = ({
  onSave,
  profile,
}: {
  onSave: (data: AntiIdealCandidateProfile) => void;
  profile: AntiIdealCandidateProfile;
}) => {
  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: profile,
  });

  const saveOnBlur = () => {
    void form.handleSubmit((data) => {
      onSave(data);
    })();
  };

  return (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-6">
        <AutosaveFormTextInput
          fieldName="title"
          form={form}
          label="Title"
          onBlur={saveOnBlur}
          placeholder="e.g., Job Hopper, Overqualified"
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="commonFailurePatterns"
          form={form}
          infoTooltip={`What characteristics do you see repeatedly in people who don't work out or don't even pass the interview?\nAre there patterns in why past hires have failed in this role?`}
          label="Common Failure Patterns"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="backgroundAndExperienceRedFlags"
          form={form}
          infoTooltip={`Are there career paths that never seem to work out here — too senior, wrong industry, etc.?\nAre there backgrounds other agencies consider relevant that actually aren't a fit?\nAre there critical skills from the JD that candidates commonly lack and are immediate disqualifiers?`}
          label="Background and Experience Red Flags"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="personalityRedFlags"
          form={form}
          infoTooltip={`What personality traits or attitudes have been warning signs in past hires?\nAre there communication styles or work habits that clash with the team or environment?`}
          label="Personality Red Flags"
          onBlur={saveOnBlur}
        />
        <AutosaveFormRichTextInput
          editorHeight="150px"
          fieldName="extraDetails"
          form={form}
          infoTooltip={`Is there anything else the client mentioned about who not to hire that didn't fit neatly into the other sections?`}
          label="Extra Details"
          onBlur={saveOnBlur}
        />
      </Column>
    </IntakeForm>
  );
};

const ProfileReadOnly = ({
  profile,
}: {
  profile: AntiIdealCandidateProfile;
}) => (
  <Column className="gap-4">
    <ReadOnlyField label="Title" value={profile.title ?? null} />
    <ReadOnlyRichTextField
      label="Common Failure Patterns"
      value={profile.commonFailurePatterns ?? null}
    />
    <ReadOnlyRichTextField
      label="Background and Experience Red Flags"
      value={profile.backgroundAndExperienceRedFlags ?? null}
    />
    <ReadOnlyRichTextField
      label="Personality Red Flags"
      value={profile.personalityRedFlags ?? null}
    />
    <ReadOnlyRichTextField
      label="Extra Details"
      value={profile.extraDetails ?? null}
    />
  </Column>
);
